import { createTheme } from '@mui/material/styles';

// Eros Innovation Brand Colors
const BLUE = '#00A8E8';        // Innovation Blue — headings, accents, technology
const GREEN = '#86E000';       // Eros Green — CTAs, highlights, growth
const DARK_BLUE = '#005B8F';   // Dark Blue — backgrounds, deep accents
const LIGHT_GREEN = '#A3D39C'; // Light Green — subtle highlights, gradients
const NEUTRAL_GRAY = '#4A4A4A'; // Neutral Gray — body text, dividers

const theme = createTheme({
  palette: {
    primary: {
      main: BLUE,
      light: '#33BBEF',
      dark: DARK_BLUE,
      contrastText: '#fff',
    },
    secondary: {
      main: GREEN,
      light: '#A0EC33',
      dark: '#6AAF00',
      contrastText: '#fff',
    },
    success: {
      main: GREEN,
      light: LIGHT_GREEN,
      dark: '#6AAF00',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    info: {
      main: BLUE,
      light: '#33BBEF',
      dark: DARK_BLUE,
    },
    background: {
      default: '#f0f7fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#0f172a',
      secondary: NEUTRAL_GRAY,
    },
  },
  typography: {
    fontFamily: '"Open Sans", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontFamily: '"Montserrat", sans-serif', fontWeight: 700 },
    h2: { fontFamily: '"Montserrat", sans-serif', fontWeight: 700 },
    h3: { fontFamily: '"Montserrat", sans-serif', fontWeight: 700, letterSpacing: '-0.02em' },
    h4: { fontFamily: '"Montserrat", sans-serif', fontWeight: 700, letterSpacing: '-0.01em' },
    h5: { fontFamily: '"Montserrat", sans-serif', fontWeight: 700 },
    h6: { fontFamily: '"Montserrat", sans-serif', fontWeight: 600 },
    subtitle1: { fontFamily: '"Montserrat", sans-serif', fontWeight: 600 },
    subtitle2: { fontFamily: '"Montserrat", sans-serif', fontWeight: 600, letterSpacing: '0.01em' },
    body1: { fontFamily: '"Open Sans", sans-serif', fontWeight: 400, lineHeight: 1.7 },
    body2: { fontFamily: '"Open Sans", sans-serif', fontWeight: 400, lineHeight: 1.6 },
    button: { fontFamily: '"Montserrat", sans-serif', fontWeight: 600 },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontFamily: '"Montserrat", sans-serif',
          borderRadius: 10,
          padding: '8px 20px',
          transition: 'all 0.2s ease',
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${DARK_BLUE} 0%, ${BLUE} 100%)`,
          boxShadow: `0 4px 15px rgba(0,168,232,0.35)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${BLUE} 0%, ${GREEN} 100%)`,
            boxShadow: `0 6px 20px rgba(0,168,232,0.45)`,
            transform: 'translateY(-1px)',
          },
        },
        containedSecondary: {
          background: `linear-gradient(135deg, ${GREEN} 0%, ${LIGHT_GREEN} 100%)`,
          boxShadow: `0 4px 15px rgba(134,224,0,0.35)`,
          color: '#fff',
          '&:hover': {
            background: `linear-gradient(135deg, #6AAF00 0%, ${GREEN} 100%)`,
            boxShadow: `0 6px 20px rgba(134,224,0,0.45)`,
            transform: 'translateY(-1px)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': { borderWidth: '1.5px' },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: `0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,168,232,0.06)`,
          border: `1px solid rgba(0,168,232,0.08)`,
          transition: 'all 0.3s ease',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 16,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontFamily: '"Open Sans", sans-serif',
          borderRadius: 8,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontFamily: '"Montserrat", sans-serif',
            fontWeight: 600,
            backgroundColor: '#f0f9ff',
            color: DARK_BLUE,
            fontSize: '0.78rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            borderBottom: `2px solid rgba(0,168,232,0.15)`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: '#f1f5f9',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            transition: 'all 0.2s ease',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: BLUE,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: '0 25px 60px rgba(0,0,0,0.15)',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          height: 6,
        },
      },
    },
  },
});

export default theme;
